import { useState, useEffect, useCallback, useRef } from "react";
import { goalApi } from "../api/goalApi";

export const useGoals = (filters = {}) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastKeyRef = useRef(null);
  const inFlightRef = useRef(false);

  const fetchGoals = useCallback(async () => {
    try {
      const key = JSON.stringify(filters || {});
      if (inFlightRef.current) return; // prevent overlapping
      if (lastKeyRef.current === key && goals.length > 0) return; // no change
      lastKeyRef.current = key;
      inFlightRef.current = true;
      setLoading(true);
      setError(null);
      const response = await goalApi.getGoals(filters);

      // Handle different response formats
      let goalData = [];
      if (response && response.data) {
        goalData = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        goalData = response;
      }

      setGoals(goalData);
    } catch (err) {
      setError(err.message || "Failed to fetch goals");
      // Set empty array on error to prevent map errors
      setGoals([]);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [filters]);

  const createGoal = useCallback(async (goalData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await goalApi.createGoal(goalData);
      setGoals((prev) => [...prev, response.data]);
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to create goal");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateGoal = useCallback(async (goalId, goalData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await goalApi.updateGoal(goalId, goalData);
      setGoals((prev) =>
        prev.map((goal) => (goal.id === goalId ? response.data : goal))
      );
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to update goal");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteGoal = useCallback(async (goalId) => {
    try {
      setLoading(true);
      setError(null);
      await goalApi.deleteGoal(goalId);
      setGoals((prev) => prev.filter((goal) => goal.id !== goalId));
    } catch (err) {
      setError(err.message || "Failed to delete goal");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Debounce slightly to avoid quick successive calls on first mount
    const t = setTimeout(() => {
      fetchGoals();
    }, 50);
    return () => clearTimeout(t);
  }, [fetchGoals]);

  return {
    goals,
    loading,
    error,
    fetchGoals,
    createGoal,
    updateGoal,
    deleteGoal,
    // React Query compatibility
    refetch: fetchGoals,
    data: goals,
    isLoading: loading,
    isError: !!error,
  };
};

// Export individual hooks for direct import
export const useCreateGoal = () => {
  const { createGoal, loading, error } = useGoals();
  return {
    mutateAsync: createGoal,
    isPending: loading,
    error,
    // React Query compatibility
    mutate: createGoal,
    isLoading: loading,
    isError: !!error,
  };
};

export const useUpdateGoal = () => {
  const { updateGoal, loading, error } = useGoals();
  return {
    mutateAsync: updateGoal,
    isPending: loading,
    error,
    // React Query compatibility
    mutate: updateGoal,
    isLoading: loading,
    isError: !!error,
  };
};

export const useDeleteGoal = () => {
  const { deleteGoal, loading, error } = useGoals();
  return {
    mutateAsync: deleteGoal,
    isPending: loading,
    error,
    // React Query compatibility
    mutate: deleteGoal,
    isLoading: loading,
    isError: !!error,
  };
};

export const useGoalsByCycle = (cycleId) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchGoals = useCallback(async () => {
    if (!cycleId) {
      setGoals([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await goalApi.getGoalsByCycle(cycleId);

      let goalData = [];
      if (response && Array.isArray(response.data)) {
        goalData = response.data;
      } else if (Array.isArray(response)) {
        goalData = response;
      }

      setGoals(goalData);
    } catch (err) {
      setError(err.message || "Failed to fetch goals for cycle");
      setGoals([]);
    } finally {
      setLoading(false);
    }
  }, [cycleId]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  return {
    goals,
    loading,
    error,
    fetchGoals,
    refetch: fetchGoals,
    data: goals,
    isLoading: loading,
    isError: !!error,
  };
};
